import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  getSettings,
  updateFeaturedVideo,
  getSubscribers,
  updateSubscriberCallsign,
  bulkDeleteSubscribers,
  getSubscriberAnalytics,
  broadcastNewEpisode,
  exportSubscribersUrl,
  adminLogin,
  adminVerify,
  setAuthToken,
  getAuthToken,
  getTips,
  deleteTip,
  updateTipStatus,
  replyToTip,
  tipFileUrl,
} from "@/lib/api";

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking, false = locked, true = unlocked
  const [pw, setPw] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [video, setVideo] = useState("");
  const [title, setTitle] = useState("");
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [tips, setTips] = useState([]);
  const [tipFilter, setTipFilter] = useState("all");

  const loadData = useCallback(async () => {
    try {
      const s = await getSettings();
      setCurrent(s);
      setVideo(s.featured_video_id || "");
      setTitle(s.featured_title || "");
      const list = await getSubscribers();
      setSubs(list);
      try {
        const a = await getSubscriberAnalytics();
        setAnalytics(a);
      } catch { /* non-fatal */ }
      const tipList = await getTips();
      setTips(tipList);
    } catch (err) {
      if (err?.response?.status === 401) lockOut();
    }
  }, []);

  const lockOut = () => {
    setAuthToken(null);
    setAuthed(false);
  };

  useEffect(() => {
    if (!getAuthToken()) {
      setAuthed(false);
      return;
    }
    adminVerify()
      .then(() => {
        setAuthed(true);
        loadData();
      })
      .catch(() => lockOut());
  }, [loadData]);

  const onLogin = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setAuthLoading(true);
    try {
      await adminLogin(pw);
      setPw("");
      setAuthed(true);
      toast.success("ACCESS GRANTED // Welcome to the Control Room.");
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Access denied.");
    } finally {
      setAuthLoading(false);
    }
  };

  const copyEmails = () => {
    const list = subs.map((s) => s.email).join(", ");
    navigator.clipboard.writeText(list).then(
      () => toast.success(`Copied ${subs.length} email${subs.length === 1 ? "" : "s"} to clipboard.`),
      () => toast.error("Could not copy.")
    );
  };

  const [editingSub, setEditingSub] = useState(null);
  const [editCallsign, setEditCallsign] = useState("");
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [purging, setPurging] = useState(false);

  const toggleSub = (id) => {
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllSubs = () => {
    setSelectedSubs((prev) => (prev.size === subs.length ? new Set() : new Set(subs.map((s) => s.id))));
  };

  const purgeSelected = async () => {
    const ids = Array.from(selectedSubs);
    if (!ids.length) return;
    if (!window.confirm(`Purge ${ids.length} agent${ids.length === 1 ? "" : "s"} from the mailing list? This cannot be undone.`)) return;
    setPurging(true);
    try {
      await bulkDeleteSubscribers(ids);
      setSubs((prev) => prev.filter((s) => !selectedSubs.has(s.id)));
      setSelectedSubs(new Set());
      toast.success(`Purged ${ids.length} agent${ids.length === 1 ? "" : "s"}.`);
      try {
        const a = await getSubscriberAnalytics();
        setAnalytics(a);
      } catch { /* non-fatal */ }
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Purge failed.");
      }
    } finally {
      setPurging(false);
    }
  };

  const saveCallsign = async (id) => {
    try {
      const updated = await updateSubscriberCallsign(id, editCallsign.trim() || null);
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, callsign: updated.callsign } : s)));
      setEditingSub(null);
      toast.success("Callsign updated.");
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Update failed.");
      }
    }
  };

  const onDeleteTip = async (id) => {
    if (!window.confirm("Delete this submission and all its files permanently?")) return;
    try {
      await deleteTip(id);
      setTips((prev) => prev.filter((t) => t.id !== id));
      toast.success("Submission purged from the Vault.");
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Delete failed.");
      }
    }
  };

  const onSetStatus = async (id, status) => {
    try {
      const updated = await updateTipStatus(id, status);
      setTips((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success(`Marked ${status}.`);
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Update failed.");
      }
    }
  };

  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplyingId] = useState(null);

  const onReply = async (id) => {
    const msg = (replyDrafts[id] || "").trim();
    if (!msg) return;
    setReplyingId(id);
    try {
      const updated = await replyToTip(id, msg);
      setTips((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setReplyDrafts((prev) => ({ ...prev, [id]: "" }));
      const last = updated.replies?.[updated.replies.length - 1];
      if (last && last.delivered === false) {
        toast.warning("Reply logged, but email delivery failed (check the source's address).");
      } else {
        toast.success("Reply sent to the source.");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Reply failed.");
      }
    } finally {
      setReplyingId(null);
    }
  };

  const fmtSize = (b) =>
    b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

  const fileSummary = (files) => {
    if (!files || files.length === 0) return null;
    const total = files.reduce((a, f) => a + (f.size || 0), 0);
    const byCat = {};
    files.forEach((f) => { byCat[f.category || "other"] = (byCat[f.category || "other"] || 0) + 1; });
    const cats = Object.entries(byCat).map(([c, n]) => `${n} ${c}`).join(", ");
    const dangerCount = files.filter((f) => f.risk === "danger").length;
    const cautionCount = files.filter((f) => f.risk === "caution").length;
    return { line: `${files.length} file${files.length === 1 ? "" : "s"} · ${fmtSize(total)} · ${cats}`, dangerCount, cautionCount };
  };
  const RISK_STYLE = {
    danger: "border-reactor-red/60 text-reactor-red",
    caution: "border-hazard-amber/60 text-hazard-amber",
    "": "border-white/15 text-white/70 hover:border-tactical-green hover:text-tactical-green",
  };

  const TIP_LABEL = { topic_request: "TOPIC REQUEST", tip_off: "TIP-OFF / LEAK" };
  const STATUS_STYLE = {
    new: "bg-hazard-amber/15 text-hazard-amber",
    reviewed: "bg-tactical-green/15 text-tactical-green",
    archived: "bg-white/10 text-white/40",
  };
  const statusCount = (s) => tips.filter((t) => t.status === s).length;
  const visibleTips = tipFilter === "all" ? tips : tips.filter((t) => (t.status || "new") === tipFilter);
  const TIP_TABS = [
    { key: "all", label: "ALL" },
    { key: "new", label: "NEW" },
    { key: "reviewed", label: "REVIEWED" },
    { key: "archived", label: "ARCHIVED" },
  ];

  const onSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updated = await updateFeaturedVideo(video, title);
      setCurrent(updated);
      setVideo(updated.featured_video_id);
      toast.success("FEED UPDATED // New transmission is live.");
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Update failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onBroadcast = async () => {
    if (!subs.length) {
      toast.error("No agents on the mailing list yet.");
      return;
    }
    const label = current?.featured_title || "the current transmission";
    if (!window.confirm(`Broadcast "${label}" to ALL ${subs.length} enlisted agent${subs.length === 1 ? "" : "s"}? This sends real email — it cannot be undone.`)) return;
    setBroadcasting(true);
    try {
      const res = await broadcastNewEpisode({});
      const failed = res?.failed || 0;
      if (failed) {
        toast.warning(`Broadcast complete: ${res.sent} sent, ${failed} failed.`);
      } else {
        toast.success(`Broadcast transmitted to ${res.sent} agent${res.sent === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Broadcast failed.");
      }
    } finally {
      setBroadcasting(false);
    }
  };

  const [previewing, setPreviewing] = useState(false);
  const onPreviewBroadcast = async () => {
    setPreviewing(true);
    try {
      const res = await broadcastNewEpisode({ preview: true });
      if (res?.sent) {
        toast.success(`Preview sent to ${res.to}. Check your inbox.`);
      } else {
        toast.error("Preview send failed — check email configuration.");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        lockOut();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.detail || "Preview failed.");
      }
    } finally {
      setPreviewing(false);
    }
  };

  // ---------------- Checking / locked states ----------------
  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-void">
        <p className="font-mono text-xs tracking-[0.3em] text-tactical-green">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse bg-tactical-green" />
          VERIFYING CLEARANCE...
        </p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-void px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="clip-hud w-full max-w-md border border-reactor-red/30 bg-bunker-shadow/80 p-8 backdrop-blur-md"
          data-testid="admin-gate"
        >
          <div className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-reactor-red">
            <span className="hazard-stripes h-3 w-10" />
            RESTRICTED · LEVEL 5 CLEARANCE
          </div>
          <h1 className="font-display text-5xl uppercase text-white">Control Room</h1>
          <p className="mt-2 font-mono text-[11px] tracking-widest text-white/40">
            root@vault:~$ ./authenticate
          </p>

          <form onSubmit={onLogin} className="mt-8" data-testid="admin-login-form">
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber">
              Access Code <span className="text-reactor-red">*</span>
            </label>
            <div className="mb-8 flex items-center border-b border-white/15 pb-2 focus-within:border-hazard-amber">
              <span className="mr-3 font-mono text-reactor-red">&gt;</span>
              <input
                type="password"
                required
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••••"
                className="w-full bg-transparent font-mono text-white placeholder:text-white/25 focus:outline-none"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={authLoading}
              className="w-full border border-reactor-red bg-reactor-red px-8 py-3.5 font-mono text-xs font-bold tracking-[0.3em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {authLoading ? "AUTHENTICATING..." : "► UNLOCK"}
            </button>
            <a
              href="/"
              className="mt-6 block text-center font-mono text-[11px] tracking-widest text-white/40 hover:text-hazard-amber"
            >
              ← BACK TO SITE
            </a>
          </form>
        </motion.div>
      </div>
    );
  }

  // ---------------- Unlocked Control Room ----------------
  return (
    <div className="min-h-screen bg-void px-6 py-16 text-white">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <a href="/" className="font-mono text-xs tracking-widest text-white/50 hover:text-hazard-amber">
            ← BACK TO SITE
          </a>
          <button
            onClick={() => { lockOut(); toast.success("Locked. Clearance revoked."); }}
            className="font-mono text-xs tracking-widest text-white/50 hover:text-reactor-red"
            data-testid="admin-logout"
          >
            LOCK ⏻
          </button>
        </div>
        <h1 className="mt-6 font-display text-5xl uppercase text-white">Control Room</h1>
        <p className="mt-2 font-mono text-xs tracking-widest text-reactor-red">
          // FEATURED TRANSMISSION CONTROL
        </p>

        {current && (
          <div className="clip-hud mt-8 border border-white/10 bg-bunker-shadow p-5">
            <p className="font-mono text-[11px] tracking-widest text-white/40">CURRENTLY LIVE</p>
            <p className="mt-2 font-mono text-sm text-tactical-green">ID: {current.featured_video_id}</p>
            <p className="font-mono text-sm text-white/70">TITLE: {current.featured_title}</p>
            <div className="mt-4 aspect-video w-full overflow-hidden border border-white/10">
              <iframe
                key={current.featured_video_id}
                className="h-full w-full"
                src={`https://www.youtube.com/embed/${current.featured_video_id}`}
                title="preview"
                allowFullScreen
              />
            </div>
          </div>
        )}

        <form onSubmit={onSave} className="mt-8 space-y-6" data-testid="admin-form">
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-hazard-amber">
              YouTube URL or Video ID
            </label>
            <input
              value={video}
              onChange={(e) => setVideo(e.target.value)}
              placeholder="https://youtu.be/... or 11-char ID"
              className="w-full border-b border-white/20 bg-transparent py-2 font-mono text-white focus:border-hazard-amber focus:outline-none"
              data-testid="admin-video-input"
              required
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-widest text-hazard-amber">
              Episode Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="LATEST TRANSMISSION"
              className="w-full border-b border-white/20 bg-transparent py-2 font-mono text-white focus:border-hazard-amber focus:outline-none"
              data-testid="admin-title-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full border border-reactor-red bg-reactor-red px-8 py-3.5 font-mono text-xs tracking-[0.3em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red disabled:opacity-60"
            data-testid="admin-save"
          >
            {loading ? "UPDATING..." : "► UPDATE LIVE FEED"}
          </button>
          <button
            type="button"
            onClick={onBroadcast}
            disabled={broadcasting || !subs.length}
            className="mt-3 w-full border border-hazard-amber px-8 py-3.5 font-mono text-xs tracking-[0.3em] text-hazard-amber transition-colors duration-100 hover:bg-hazard-amber hover:text-void disabled:cursor-not-allowed disabled:opacity-30"
            data-testid="admin-broadcast"
          >
            {broadcasting
              ? "TRANSMITTING TO ALL AGENTS..."
              : `► BROADCAST TO MAILING LIST (${subs.length})`}
          </button>
          <button
            type="button"
            onClick={onPreviewBroadcast}
            disabled={previewing || broadcasting}
            className="mt-2 w-full border border-white/20 px-8 py-2.5 font-mono text-[11px] tracking-[0.3em] text-white/60 transition-colors duration-100 hover:border-tactical-green hover:text-tactical-green disabled:cursor-not-allowed disabled:opacity-30"
            data-testid="admin-broadcast-preview"
          >
            {previewing ? "SENDING PREVIEW..." : "✉ SEND PREVIEW TO MY INBOX"}
          </button>
        </form>

        {/* Subscribers panel */}
        <div className="mt-16" data-testid="subscribers-panel">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-mono text-xs tracking-widest text-reactor-red">// ENLISTED AGENTS</p>
              <h2 className="mt-2 font-display text-4xl uppercase text-white">Mailing List</h2>
              <p className="mt-1 font-mono text-xs text-tactical-green" data-testid="subs-count">
                {String(subs.length).padStart(4, "0")} ON THE FREQUENCY
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={copyEmails}
                disabled={!subs.length}
                className="border border-white/25 px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] text-white/80 transition-colors duration-100 hover:border-hazard-amber hover:text-hazard-amber disabled:opacity-40"
                data-testid="copy-emails"
              >
                COPY EMAILS
              </button>
              <button
                onClick={purgeSelected}
                disabled={!selectedSubs.size || purging}
                className="border border-reactor-red/60 px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] text-reactor-red transition-colors duration-100 hover:bg-reactor-red hover:text-void disabled:cursor-not-allowed disabled:opacity-30"
                data-testid="purge-selected-subs"
              >
                {purging ? "PURGING..." : `✕ PURGE SELECTED (${selectedSubs.size})`}
              </button>
              <a
                href={exportSubscribersUrl()}
                className="border border-reactor-red bg-reactor-red px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red"
                data-testid="download-csv"
              >
                ↓ DOWNLOAD CSV
              </a>
            </div>
          </div>

          {analytics && analytics.total > 0 && (
            <div className="mt-6 border border-white/10 bg-bunker-shadow/50 p-5" data-testid="location-analytics">
              <div className="mb-4 flex items-baseline justify-between font-mono text-[11px] tracking-[0.25em]">
                <span className="text-hazard-amber">// GEOGRAPHIC DISTRIBUTION</span>
                <span className="text-white/40">
                  {analytics.resolved}/{analytics.total} RESOLVED
                  {analytics.unknown > 0 && <span className="text-white/25"> · {analytics.unknown} UNKNOWN</span>}
                </span>
              </div>
              {analytics.by_country.length === 0 ? (
                <p className="font-mono text-xs text-white/40">No geolocated agents yet.</p>
              ) : (
                <ul className="space-y-2">
                  {analytics.by_country.slice(0, 6).map((c) => {
                    const pct = analytics.total > 0 ? Math.round((c.count / analytics.total) * 100) : 0;
                    return (
                      <li key={c.country} className="flex items-center gap-3 font-mono text-xs" data-testid={`analytics-row-${c.country}`}>
                        <span className="w-40 shrink-0 truncate text-white/80">{c.country}</span>
                        <div className="relative h-2 flex-1 bg-white/5">
                          <div
                            className="absolute inset-y-0 left-0 bg-tactical-green/70"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-14 shrink-0 text-right text-white/60">{c.count}</span>
                        <span className="w-10 shrink-0 text-right text-white/30">{pct}%</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {subs.length === 0 ? (
            <p className="mt-8 font-mono text-sm text-white/40">No agents enlisted yet.</p>
          ) : (
            <div className="clip-hud mt-6 overflow-hidden border border-white/10 bg-bunker-shadow">
              <table className="w-full border-collapse text-left font-mono text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-hazard-amber">
                    <th className="px-4 py-3 tracking-widest">
                      <input
                        type="checkbox"
                        checked={subs.length > 0 && selectedSubs.size === subs.length}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedSubs.size > 0 && selectedSubs.size < subs.length;
                        }}
                        onChange={toggleAllSubs}
                        className="h-3.5 w-3.5 cursor-pointer accent-reactor-red"
                        data-testid="select-all-subs"
                        aria-label="Select all subscribers"
                      />
                    </th>
                    <th className="px-4 py-3 tracking-widest">#</th>
                    <th className="px-4 py-3 tracking-widest">EMAIL</th>
                    <th className="px-4 py-3 tracking-widest">CALLSIGN</th>
                    <th className="px-4 py-3 tracking-widest">ENLISTED</th>
                    <th className="px-4 py-3 tracking-widest">COUNTRY</th>
                    <th className="px-4 py-3 tracking-widest">CITY</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s, i) => (
                    <tr key={s.id || s.email} className={`border-b border-white/5 transition-colors ${selectedSubs.has(s.id) ? "bg-reactor-red/[0.06] text-white" : "text-white/70 hover:bg-gunmetal"}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedSubs.has(s.id)}
                          onChange={() => toggleSub(s.id)}
                          className="h-3.5 w-3.5 cursor-pointer accent-reactor-red"
                          data-testid={`select-sub-${s.id}`}
                          aria-label={`Select ${s.email}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-white/30">{String(subs.length - i).padStart(3, "0")}</td>
                      <td className="px-4 py-3 text-white">{s.email}</td>
                      <td className="px-4 py-3 text-porthole-blue">
                        {editingSub === s.id ? (
                          <span className="flex items-center gap-2">
                            <input
                              value={editCallsign}
                              onChange={(e) => setEditCallsign(e.target.value)}
                              className="w-28 border-b border-hazard-amber bg-transparent font-mono text-white focus:outline-none"
                              data-testid={`callsign-edit-input-${s.id}`}
                              autoFocus
                            />
                            <button onClick={() => saveCallsign(s.id)} className="text-tactical-green hover:text-white" data-testid={`callsign-save-${s.id}`}>✓</button>
                            <button onClick={() => setEditingSub(null)} className="text-white/40 hover:text-reactor-red">✕</button>
                          </span>
                        ) : (
                          <span className="group flex items-center gap-2">
                            {s.callsign || "—"}
                            <button
                              onClick={() => { setEditingSub(s.id); setEditCallsign(s.callsign || ""); }}
                              className="text-white/25 opacity-0 transition-opacity group-hover:opacity-100 hover:text-hazard-amber"
                              title="Edit callsign"
                              data-testid={`callsign-edit-${s.id}`}
                            >
                              ✎
                            </button>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/40">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-white/70">{s.country || "—"}</td>
                      <td className="px-4 py-3 text-white/50">{s.city || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Intel / Tips panel */}
        <div className="mt-16" data-testid="tips-panel">
          <div className="border-b border-white/10 pb-5">
            <p className="font-mono text-xs tracking-widest text-hazard-amber">// INTEL DROP</p>
            <h2 className="mt-2 font-display text-4xl uppercase text-white">Tips &amp; Requests</h2>
            <p className="mt-1 font-mono text-xs text-tactical-green" data-testid="tips-count">
              {String(tips.length).padStart(4, "0")} ON RECORD · {statusCount("new")} NEW
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {TIP_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setTipFilter(tab.key)}
                  className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] transition-colors ${
                    tipFilter === tab.key
                      ? "border-hazard-amber text-hazard-amber"
                      : "border-white/15 text-white/40 hover:border-white/40"
                  }`}
                  data-testid={`tip-filter-${tab.key}`}
                >
                  {tab.label}
                  {tab.key !== "all" ? ` (${statusCount(tab.key)})` : ` (${tips.length})`}
                </button>
              ))}
            </div>
          </div>

          {visibleTips.length === 0 ? (
            <p className="mt-8 font-mono text-sm text-white/40">
              {tips.length === 0 ? "No intel received yet." : "Nothing in this queue."}
            </p>
          ) : (
            <div className="mt-6 space-y-4">
              {visibleTips.map((t) => (
                <div
                  key={t.id}
                  className="clip-hud border border-white/10 bg-bunker-shadow p-5 md:p-6"
                  data-testid={`tip-${t.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-3">
                    <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] tracking-widest">
                      <span
                        className={`px-2 py-1 ${
                          t.type === "tip_off"
                            ? "bg-reactor-red/15 text-reactor-red"
                            : "bg-porthole-blue/15 text-porthole-blue"
                        }`}
                      >
                        {TIP_LABEL[t.type] || t.type}
                      </span>
                      <span
                        className={`px-2 py-1 ${STATUS_STYLE[t.status || "new"]}`}
                        data-testid={`tip-status-${t.id}`}
                      >
                        {(t.status || "new").toUpperCase()}
                      </span>
                      <span className="text-white/40">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 font-mono text-[11px] tracking-widest">
                      {(t.status || "new") !== "reviewed" && (
                        <button
                          onClick={() => onSetStatus(t.id, "reviewed")}
                          className="text-white/40 transition-colors hover:text-tactical-green"
                          data-testid={`tip-review-${t.id}`}
                        >
                          ✓ REVIEWED
                        </button>
                      )}
                      {(t.status || "new") !== "archived" ? (
                        <button
                          onClick={() => onSetStatus(t.id, "archived")}
                          className="text-white/40 transition-colors hover:text-hazard-amber"
                          data-testid={`tip-archive-${t.id}`}
                        >
                          ▤ ARCHIVE
                        </button>
                      ) : (
                        <button
                          onClick={() => onSetStatus(t.id, "new")}
                          className="text-white/40 transition-colors hover:text-hazard-amber"
                          data-testid={`tip-restore-${t.id}`}
                        >
                          ↺ RESTORE
                        </button>
                      )}
                      <button
                        onClick={() => onDeleteTip(t.id)}
                        className="text-white/40 transition-colors hover:text-reactor-red"
                        data-testid={`tip-delete-${t.id}`}
                      >
                        DELETE ✕
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-1 font-mono text-xs md:text-[13px]">
                    <p className="text-white/50">
                      FROM: <span className="text-white">{t.name || "Anonymous"}</span>
                    </p>
                    <p className="text-white/50">
                      CONTACT:{" "}
                      <a href={`mailto:${t.email}`} className="text-hazard-amber hover:underline">
                        {t.email}
                      </a>
                    </p>
                  </div>

                  <p className="mt-4 whitespace-pre-wrap border-l-2 border-hazard-amber/40 pl-4 font-body text-sm font-light leading-relaxed text-white/80 md:text-[15px]">
                    {t.message}
                  </p>

                  {t.files && t.files.length > 0 && (() => {
                    const sum = fileSummary(t.files);
                    return (
                    <div className="mt-5" data-testid={`tip-attachments-${t.id}`}>
                      <div className="mb-2 flex flex-wrap items-center gap-2 font-mono text-[10px] tracking-widest">
                        <span className="text-white/40" data-testid={`tip-attach-summary-${t.id}`}>
                          {sum.line.toUpperCase()}
                        </span>
                        {sum.dangerCount > 0 && (
                          <span className="bg-reactor-red/15 px-2 py-1 text-reactor-red" data-testid={`tip-risk-flag-${t.id}`}>
                            ⚠ {sum.dangerCount} RISKY FILE{sum.dangerCount === 1 ? "" : "S"} — DO NOT OPEN UNVERIFIED
                          </span>
                        )}
                        {sum.cautionCount > 0 && sum.dangerCount === 0 && (
                          <span className="bg-hazard-amber/15 px-2 py-1 text-hazard-amber">
                            ⚠ {sum.cautionCount} ARCHIVE — CONTENTS UNKNOWN
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {t.files.map((f) => (
                          <a
                            key={f.file_id}
                            href={tipFileUrl(t.id, f.file_id)}
                            className={`flex items-center gap-2 border px-3 py-2 font-mono text-[11px] transition-colors ${RISK_STYLE[f.risk] || RISK_STYLE[""]}`}
                            data-testid={`tip-file-${f.file_id}`}
                            title={f.risk === "danger" ? "Potentially executable — scan before opening" : f.risk === "caution" ? "Archive — contents unknown until extracted" : f.category}
                          >
                            {f.risk === "danger" ? "⚠" : "↓"} {f.filename}
                            <span className="opacity-40">· {f.category} · {fmtSize(f.size)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                    );
                  })()}

                  {/* Reply thread */}
                  {t.replies && t.replies.length > 0 && (
                    <div className="mt-5 space-y-2" data-testid={`tip-thread-${t.id}`}>
                      <p className="font-mono text-[10px] tracking-widest text-white/40">
                        THREAD ({t.replies.length})
                      </p>
                      {t.replies.map((r, ri) => (
                        <div key={ri} className="border-l-2 border-tactical-green/40 bg-void/40 px-4 py-2">
                          <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-tactical-green">
                            <span>YOU · {r.sent_at ? new Date(r.sent_at).toLocaleString() : ""}</span>
                            {r.delivered === false && <span className="text-reactor-red">DELIVERY FAILED</span>}
                          </div>
                          <p className="mt-1 whitespace-pre-wrap font-body text-sm font-light text-white/75 md:text-[15px]">{r.body}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply composer */}
                  <div className="mt-5">
                    <textarea
                      rows={2}
                      value={replyDrafts[t.id] || ""}
                      onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [t.id]: e.target.value }))}
                      placeholder={`Reply securely to ${t.email}...`}
                      className="w-full resize-y border border-white/15 bg-void/50 p-3 font-mono text-xs text-white placeholder:text-white/25 focus:border-hazard-amber focus:outline-none md:text-[13px]"
                      data-testid={`tip-reply-input-${t.id}`}
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-widest text-white/30">
                        SENDS FROM THE VAULT · REPLY-TO ROUTES BACK TO YOU
                      </span>
                      <button
                        onClick={() => onReply(t.id)}
                        disabled={replyingId === t.id || !(replyDrafts[t.id] || "").trim()}
                        className="border border-tactical-green bg-tactical-green/10 px-5 py-2 font-mono text-[11px] tracking-[0.25em] text-tactical-green transition-colors hover:bg-tactical-green hover:text-void disabled:opacity-40"
                        data-testid={`tip-reply-send-${t.id}`}
                      >
                        {replyingId === t.id ? "SENDING..." : "► SEND REPLY"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
